import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { NavigationService } from '../../../../core/navigation.service';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchEvent } from '../../../../shared/search/models/search-event';
import { NEW_SEARCH_EVENT, ITEM_CLICK_EVENT, NEW_ROW_EVENT } from '../../../../shared/search/search-constants';
import { SearchStatus } from '../../../../shared/search/models/search-status.model';
import { ResourceService } from 'sce-core';
import { SearchableEntityServiceFactory } from '../../../services/searchable-entity/searchable-entity-service-factory';
import { SearchableEntityConfig } from '../../../models/searchable-entity-config';
import { AlertService } from 'sce-core';
import { ActivatedRoute } from '@angular/router';
import { PlatformLocation, Location } from '@angular/common';
import { UserPreferenceService } from '../../../../layout/home/services/user-preference.service';
import { DELETE_FAVOURITE, ADD_FAVOURITE, UserPreferenceEvent, ADD_RECENT } from '../../../models/user-favourite-event';
import { SavedSearchService } from '../../../search/saved-search.service';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../shared-service-registry';
import { Preference } from '../../../search/models/preference.model';
import { TablePreference } from '../../../search/models/table-preference.model';
import { DataTableEditorService } from '../../../search/results/data-table-editor.service';
import { SearchCriteria } from '../../../search/models/search-filter-model';
import { SimpleEntityDataService } from '../../../services/searchable-entity/simple-entity/simple-entity-data.service';
import { SimpleEntityButtonService } from '../../../services/searchable-entity/simple-entity/simple-entity-button.service';
import {TooltipModule} from 'primeng/primeng';
@Component({
  selector: 'lfwms-multitab-searchable-entity-search',
  templateUrl: './multitab-search-page.component.html',
  styleUrls: ['./multitab-search-page.component.css']
})
export class MultitabSearchableEntitySearchComponent implements OnInit, OnDestroy {

  // The factory for all the required services
  @Input() public serviceFactory: SearchableEntityServiceFactory;

  // The configuration object for the entity
  public entityConfig: SearchableEntityConfig;

  // helper services
  protected navService: NavigationService;
  protected resourceService: ResourceService;
  public savedSearchService: SavedSearchService;
  protected alertService: AlertService;
  protected userPreferenceService: UserPreferenceService;

  // entity services
  public dataService: SimpleEntityDataService;
  public buttonService: SimpleEntityButtonService;
  public dataEditor: DataTableEditorService;

  private tablePreference: TablePreference;
  public favouriteIconFlag: boolean = false;
  public expandFilterAccordion: boolean = true;
  protected expandResultsAccordion: boolean = false;
  protected searchEventSubscription: Subscription;
  protected userPreferenceEventSubscription: Subscription;
  public searchService: SearchService;
  public searchStatus: SearchStatus;
  public successMsg: string = '';
  public panel: any;
  public default: any;
  public tooltipPosition: string = 'bottom';
  public tooltipMessage: string = 'Add To Favourites';

  constructor(private coreServiceRegistry: CoreServiceRegistry, private sharedServiceRegistry: SharedServiceRegistry,
    private activatedRoute: ActivatedRoute, public platformLocation: PlatformLocation,
    public location: Location) {
    this.navService = coreServiceRegistry.navigationService;
    this.resourceService = coreServiceRegistry.resourceService;
    this.userPreferenceService = coreServiceRegistry.userPreferencesService;
    this.savedSearchService = sharedServiceRegistry.savedSearchService;
    this.alertService = sharedServiceRegistry.alertService;
  }

  public ngOnInit() {
    this.initializeProperties();
    this.subscribeForEvents();
    this.activatedRoute.params.subscribe(params => {
      const searchId = params['searchId'];
      this.expandFilterAccordion = true;
      // if there is a search id in the route, load it
      if (searchId) {
        this.loadFromSavedSearchCriteria(searchId);
      }
      this.navService.setPageTitle(this.entityConfig.getItemLabel().concat(' ').concat(this.resourceService.get('lbl_search')));
    });
    this.dataService.setDisplayMode('V');
    this.dataService.selectedTab = 'mainTab';
  }

  /**
   * Initialize the component properties
   */
  private initializeProperties() {
    this.dataService = <SimpleEntityDataService>this.serviceFactory.getDataService();
    this.dataEditor = this.serviceFactory.getDataEditor();
    this.buttonService = <SimpleEntityButtonService>this.serviceFactory.getButtonService();
    this.entityConfig = this.dataService.getEntityConfiguration();
    this.searchService = this.dataService.getSearchService();
    this.searchStatus = this.searchService.searchStatus;
    this.searchService.searchParams.tableEditor = this.dataEditor;
    this.searchService.clearSavedSearchConfig(); // Clear any old data
    this.searchService.fetchMetadata()
      .subscribe((metadata: any) => {
        this.initializeTablePreferences();
      });
    // if the search filter is not displayed, then do a search on page load
    if (!this.entityConfig.showSearchFilter) {
      if (!this.searchService.getSearchCriteria()) {
        this.searchService.setSearchCriteria(new SearchCriteria());
      }
      this.searchService.performSearch();
    }
    this.dataService.initializeMultiTabbedDetailContexts(this.dataService.tableIds, this.dataService.primaryIdProperties);
    this.buttonService.initializeSearchPageButtons();
  }

  /**
   * Initialize the page based on the user preferences for the current search
   */
  private initializeTablePreferences() {
    this.tablePreference = this.searchService.getTablePreference();
    const favPreference: Preference = this.tablePreference.favouriteConfig;
    if (favPreference && favPreference.configuration) {
      this.favouriteIconFlag = favPreference.configuration.favourite;
    }
  }

  /**
   * Subscribe for the search and user preference events
   */
  private subscribeForEvents() {
    this.searchEventSubscription = this.searchService.receiveEvent()
      .subscribe((event: SearchEvent) => {
        if (event.type === NEW_SEARCH_EVENT) {
          this.dataService.searchService = this.searchService;
          this.buttonService.initializeSearchPageButtons();
          this.expandFilterAccordion = false;
          this.expandResultsAccordion = true;
        } else if (event.type === ITEM_CLICK_EVENT) {
          // this.dataService.setPrimarySelectedDocument(event.selectedItemId);
          this.showDetailsPage(event.selectedItemId);
        } else if (event.type === NEW_ROW_EVENT) {
          this.dataService.handleAddNewRow(event); // added to autogenerate primary key while adding a new row
        }
      });

    this.userPreferenceEventSubscription = this.userPreferenceService.receiveEvent()
      .subscribe((event: UserPreferenceEvent) => {
        if (event.source === 'search') {
          // if this event originated from the search page, ignore it. It will be handled separately
          return;
        }
        if (event.type === DELETE_FAVOURITE) {
          this.favouriteIconFlag = false;
          // this.userPreferences.preferenceId = null;
        } else if (event.type === ADD_FAVOURITE) {
          this.favouriteIconFlag = true;
        }
      });
  }

  /**
   * If the page is loaded using a search criteria id, prepopulate the search filter
   * @param searchId - The search id
   */
  private loadFromSavedSearchCriteria(searchId) {
    // clear the search results
    this.searchStatus.hasResults = false;
    this.savedSearchService.loadSearchCriteria(searchId, this.searchService.getSavedSearchConfig())
      .subscribe((resp: any) => {
        if (resp['statusCode'] && resp['statusMessage']) {
          if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
            const formattedCriteria = this.savedSearchService.transformSearchCriteriaToModel(resp, this.searchService);
            if (!formattedCriteria) {
              this.alertService.error('Invalid search criteria');
              return;
            }
            this.searchService.setSearchCriteria(formattedCriteria);
            this.searchService.setNonMandatoryConditionsCount(0);
            this.searchService.getSearchCriteria().conditions.forEach((condition) => {
              if (condition.isMandatory === false) {
                this.searchService.setNonMandatoryConditionsCount(this.searchService.getNonMandatoryConditionsCount() + 1);
              }
            });
          }
        } else {
          if (resp.error) {
            this.alertService.clearAll();
            this.alertService.error(resp.error);
          }
        }
      }, (error) => {
        this.alertService.clearAll().error(error);
      });
  }

  // method used to translate the labels
  public getLabel(key) {
    return this.resourceService.get(key);
  }

  /**
   * This method opens the details page for the selected items
   *
   * @param id - item id
   * @param isCreateItem - is it a create event
   */
  protected showDetailsPage(id, isCreateItem?: boolean) {
    if (isCreateItem) {
      this.dataService.setDisplayMode('C');
    } else {
      this.dataService.setDisplayMode('V');
    }
    //  this.navService.navigateByUrl(this.entityConfig.getDetailPageRoute());
    const selectedDetailrows = this.searchService.getSelectedRows();
    let isExternal: boolean;
    if (selectedDetailrows.length > 0) {
      selectedDetailrows.forEach(element => {
        const IdcolumnName = this.searchService.searchParams.idProperty;
        const detailPageUrl = (this.platformLocation as any).location.origin
          + this.location['_baseHref'] +
          this.entityConfig.getDetailPageRoute() + '/' + element.cells[IdcolumnName].cVal;
        window.open(detailPageUrl);
      });
      isExternal = true;
    }
    if (!(this.checkIsSelectedDetail(id, selectedDetailrows))) {
      this.navService.navigateWithRouteParams(this.entityConfig.getDetailPageRoute(), [id]);
    }
    this.notifyUserPreferenceEvent(ADD_RECENT, isExternal);
  }

  public checkIsSelectedDetail(selectedId, selectedDetailrows) {
    let isIdSelected = false;
    selectedDetailrows.map(element => {
      const IdcolumnName = this.searchService.searchParams.idProperty;
      if (selectedId === element.cells[IdcolumnName].cVal) {
        isIdSelected = true;
      }
    });
    return isIdSelected;
  }

  // add/remove favorite config
  public toggleFavourites() {
    // if the page is set as favourite
    if (this.favouriteIconFlag === false) {
      const userPreferences = {
        dataTableId: this.searchService.searchParams.tableId,
        prefType: 'FAVOURITECONFIG',
        configuration: { 'favourite': true }
      };
      this.userPreferenceService.saveTablePreferences(userPreferences).subscribe((data: any) => {
        this.favouriteIconFlag = true;
        this.tablePreference.favouriteConfig = data;
        //   this.userPreferences.preferenceId = data['preferenceId'];
        this.notifyUserPreferenceEvent(ADD_FAVOURITE);
        this.alertService.success(this.resourceService.get('msg_fav_add'));
      }, (error: any) => {
        this.alertService.clearAll().error(this.resourceService.get(error.error.errors['0'].validationMessage));
      });
    } else {
      this.userPreferenceService.deleteFavorites(this.tablePreference.favouriteConfig.preferenceId).subscribe((data: any) => {
        this.favouriteIconFlag = false;
        this.tablePreference.favouriteConfig = null;
        this.alertService.success(this.resourceService.get('msg_fav_del'));
        this.notifyUserPreferenceEvent(DELETE_FAVOURITE);
      });
    }
  }

  private notifyUserPreferenceEvent(type: number, isExternal?: boolean) {
    const event = new UserPreferenceEvent();
    event.type = type;
    event.source = 'search';
    if (isExternal) {
      event.isExternal = isExternal;
    }
    this.userPreferenceService.notifyEvent(event);
  }

  public toggleFilterAccordion() {
    this.expandFilterAccordion = !this.expandFilterAccordion;
  }

  public toggleResultsAccordion() {
    this.expandResultsAccordion = !this.expandResultsAccordion;
  }

  /**
   * Handle the button actions on page. The call is delegated to the corresponding button service
   */
  public handleButtonPanelActions(panelId: string, action: any) {
    this.buttonService.performButtonActions(action);
  }


  // LFWM-441 method for handling tab change
  public onTabChange(event) {
    this.dataService.selectedTab = event; // .index;
    if (event === '0') {
      this.searchService.performSearch();
      if (this.buttonService) {
        this.searchService.searchParams.actionButtons = this.buttonService.getButtonsForPanel('search-table');
      }
    } else {
      this.dataService.searchServices[event - 1].setSearchCriteria(this.searchService.getSearchCriteria());
      this.dataService.searchServices[event - 1].performSearch();
    }
   }
  /**
   * Cleanup the subscriptions
   */
  public ngOnDestroy() {
    this.searchService.setNonMandatoryConditionsCount(0);


    if (this.searchEventSubscription) {
      this.searchEventSubscription.unsubscribe();
    }
    if (this.userPreferenceEventSubscription) {
      this.userPreferenceEventSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/components/searchable-entity/multitab-search-page/multitab-search-page.component.ts